import React, { useEffect, useState } from 'react';
import { HeartIcon, ChatBubbleLeftIcon, ArrowPathRoundedSquareIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ThemeBadge from '../components/ThemeBadge';
import NewPostModal from '../components/NewPostModal';
import ThemePieChart from '../components/charts/ThemePieChart';
import TrendLineChart from '../components/charts/TrendLineChart';
import LocalityBarChart from '../components/charts/LocalityBarChart';
import WorldMap from '../components/charts/WorldMap';
import { THEMES, formatDate } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || '';

function StatPill({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function PostCard({ post, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="w-full rounded-xl border border-slate-200 bg-white p-5 text-left shadow-card transition hover:border-brand-200 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">
            {post.influencer?.display_name || 'Perfil sem identificação'}
            {post.influencer?.platform && (
              <span className="ml-2 text-xs font-normal text-slate-400">· {post.influencer.platform}</span>
            )}
          </p>
          <p className="text-xs text-slate-400">{formatDate(post.published_at)}</p>
        </div>
        {post.relevance_score != null && (
          <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
            {post.relevance_score}% relevante
          </span>
        )}
      </div>
      <ThemeBadge value={post.theme} className="mt-3" />
      <p className="mt-3 line-clamp-3 text-sm text-slate-600">{post.content}</p>
      <div className="mt-4 flex gap-4 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1"><HeartIcon className="h-4 w-4" />{post.likes || 0}</span>
        <span className="inline-flex items-center gap-1"><ChatBubbleLeftIcon className="h-4 w-4" />{post.comments_count || 0}</span>
        <span className="inline-flex items-center gap-1"><ArrowPathRoundedSquareIcon className="h-4 w-4" />{post.shares || 0}</span>
      </div>
    </button>
  );
}

function Dashboard({ user, onLogout }) {
  const [posts, setPosts] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [stats, setStats] = useState(null);
  const [theme, setTheme] = useState('');
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPosts = async () => {
    const params = theme ? { theme } : {};
    const { data } = await axios.get(`${API_URL}/api/posts`, { params, headers });
    setPosts(data.posts || []);
  };

  const fetchRankings = async () => {
    const endpoint = period === 'week' ? '/api/rankings/week' : '/api/rankings/month';
    const { data } = await axios.get(`${API_URL}${endpoint}`, { headers });
    setRankings(data.rankings || []);
  };

  const fetchStats = async () => {
    const params = theme ? { theme, days: 30 } : { days: 30 };
    const { data } = await axios.get(`${API_URL}/api/news/stats`, { params });
    setStats(data);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchPosts(), fetchRankings(), fetchStats()])
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, period]);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-500">Panorama de influência digital sobre adolescentes.</p>
          </div>
          <button
            onClick={() => setShowNewPost(true)}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            + Cadastrar post
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatPill label="Notícias (30d)" value={stats?.total ?? '—'} />
          <StatPill label="Relevância média" value={stats ? `${stats.avg_relevance}%` : '—'} />
          <StatPill label="Posts cadastrados" value={posts.length} />
          <StatPill label="Tema em foco" value={theme ? 'Filtrado' : 'Todos'} />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setTheme('')}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset ${
              theme === '' ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
            }`}
          >
            Todos os temas
          </button>
          {THEMES.map((t) => (
            <button
              key={t.value}
              onClick={() => setTheme(t.value)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset ${
                theme === t.value ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Visão geral: contadores, tendência, pizza e mapa — reagem ao filtro de tema acima */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <ChartCard title="Por tema" subtitle="Últimos 30 dias">
            <ThemePieChart data={stats?.by_theme} />
          </ChartCard>
          <ChartCard title="Tendência" subtitle="Notícias coletadas por dia">
            <TrendLineChart data={stats?.by_day} />
          </ChartCard>
          <ChartCard title="Por localidade" subtitle="Aproximada, pelo idioma da busca">
            <LocalityBarChart data={stats?.by_locality} />
          </ChartCard>
        </div>

        <ChartCard title="Mapa-múndi" subtitle={theme ? `Filtrado por ${THEMES.find((t) => t.value === theme)?.label}` : 'Todos os temas'}>
          <WorldMap data={stats?.by_country} theme={theme} />
        </ChartCard>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="mb-3 text-lg font-semibold text-slate-800">Posts recentes</h2>
            {loading ? (
              <p className="text-sm text-slate-400">Carregando…</p>
            ) : posts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">
                Nenhum post cadastrado ainda. Cadastre um post encontrado numa rede social, ou promova um lead a partir das{' '}
                <button className="text-brand-600 underline" onClick={() => navigate('/leads')}>leads</button>.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {posts.map((post) => (
                  <PostCard key={post.id} post={post} onOpen={() => navigate(`/posts/${post.id}`)} />
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">Rankings</h2>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600"
              >
                <option value="week">Semana</option>
                <option value="month">Mês</option>
              </select>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
              {rankings.length === 0 ? (
                <p className="text-sm text-slate-400">Sem dados no período.</p>
              ) : (
                <ol className="flex flex-col gap-3">
                  {rankings.map((r, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-slate-700">{r.title}</p>
                        <p className="text-xs text-slate-400">Score {r.score}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>

      {showNewPost && (
        <NewPostModal onClose={() => setShowNewPost(false)} onCreated={() => { setShowNewPost(false); fetchPosts(); }} />
      )}
    </Layout>
  );
}

export default Dashboard;
